import { IconWrapper, IStyles, pxToRem, pxTovW } from '@geneo2-web/shared-ui';
import { Box, Dialog, Divider, IconButton, Typography } from '@mui/material';
import React from 'react';
import { ISummaryData } from '../../utils_assessment/assessmentUtils';

const styles: IStyles = {
  dialogPaper: {
    width: { xs: '95%', md: pxTovW(650) },
    maxWidth: 'none',
    overflow: 'visible', // Essential for the overlapping close button
    position: 'relative',
    background: 'transparent',
    boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.0)',
    display: 'flex',
    alignItems: 'center',
    gap: pxToRem(10),
  },
  closeButtonBox: {
    position: 'absolute',
    top: pxToRem(-18),
    right: pxToRem(-18),
    zIndex: 10,
  },
  closeButton: {
    backgroundColor: '#FFC600', // Yellow from the image
    color: '#fff',
    '&:hover': {
      backgroundColor: '#E6B200',
    },
    width: pxToRem(44),
    height: pxToRem(44),
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    p: { xs: pxToRem(20), md: `${pxToRem(24)} ${pxToRem(32)}` },
    gap: pxToRem(10),
  },
  headerIconWrapper: {
    width: pxToRem(64),
    height: pxToRem(64),
    borderRadius: '50%',
    backgroundColor: '#DFF0FE', // Light blue background
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    mr: pxToRem(16),
  },
  composedIconContainer: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontWeight: 700,
    color: '#333',
    fontSize: { xs: pxToRem(22), md: pxToRem(28) },
  },
  divider: {
    borderColor: '#E5E7EB',
  },
  contentContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)', // ✅ exactly 3 per row
    gap: pxToRem(20),
    p: { xs: pxToRem(20), md: `${pxToRem(32)} ${pxToRem(20)} ${pxToRem(40)}` },
    justifyContent: 'center',
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    borderRight: '1px solid #F3F4F6',
    '&:nth-of-type(3n)': {
      borderRight: 'none',
    },
    minWidth: { md: pxTovW(350), lg: pxTovW(220) },
  },
  subjectTag: {
    backgroundColor: '#0D7BDE', // Blue tag
    color: 'white',
    padding: `${pxToRem(6)} ${pxToRem(24)}`,
    borderRadius: pxToRem(12),
    fontWeight: 700,
    fontSize: { xs: pxToRem(14), md: pxToRem(18) },
    mb: pxToRem(20),
    whiteSpace: 'nowrap',
  },
  statRow: {
    display: 'flex',
    alignItems: 'center',
    gap: pxToRem(8),
    mb: pxToRem(4),
  },
  statNumber: {
    fontWeight: 800,
    color: '#333',
    lineHeight: 1,
  },
  statLabel: {
    // fontSize: { xs: pxToRem(14), md: pxToRem(16) },
    color: '#888',
    fontWeight: 500,
    textAlign: { xs: 'center' },
  },
  verticalDivider: {
    borderColor: '#F3F4F6',
    borderWidth: pxToRem(1),
    mx: { xs: pxToRem(1), md: pxToRem(8) },
  },
};

interface IProps {
  open: boolean;
  handleClose?: () => void;
  data?: ISummaryData[];
}

export const OverallSummaryPopup = (props: IProps) => {
  const { open, handleClose, data } = props;

  // Default data mapping the image
  // const summaryData = [
  // { subject: 'Science', attempted: 10, total: 50 },
  // { subject: 'Mathematics', attempted: 23, total: 40 },
  // { subject: 'English', attempted: 11, total: 20 },
  // { subject: 'English', attempted: 11, total: 20 },
  // { subject: 'English', attempted: 11, total: 20 },
  // ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: styles.dialogPaper,
      }}
    >
      <IconButton sx={styles.closeButton} onClick={handleClose}>
        <IconWrapper
          name="close-yellow"
          size="md"
          parentFolder="icons"
          type="png"
          customSx={{
            width: { xs: pxToRem(33), md: pxTovW(54) },
            height: { xs: pxToRem(33), md: pxTovW(54) },
          }}
        />
      </IconButton>
      <Box
        sx={{
          background: 'white',
          borderRadius: pxToRem(8),
        }}
      >
        {/* Header Section */}
        <Box sx={styles.headerContainer}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: { xs: pxToRem(33), md: pxTovW(60) },
              height: { xs: pxToRem(33), md: pxTovW(60) },
              borderRadius: '50%',
              background: '#CCE9FF',
            }}
          >
            <IconWrapper
              name="question-blue"
              size="md"
              parentFolder="icons"
              type="png"
              customSx={{
                width: { xs: pxToRem(20), md: pxTovW(35) },
                height: { xs: pxToRem(20), md: pxTovW(35) },
              }}
            />
          </Box>
          <Typography variant="h2" sx={styles.headerTitle}>
            Overall Summary
          </Typography>
        </Box>

        <Divider sx={styles.divider} />

        {/* Content Section */}
        <Box
          sx={{
            ...styles.contentContainer,
            gridTemplateColumns: `repeat(${Math.min(
              data?.length || 1,
              3
            )}, 1fr)`,
          }}
        >
          {data?.map((item, index) => (
            <React.Fragment key={item.subject}>
              <Box sx={styles.column}>
                <Box sx={styles.subjectTag}>{item.subject}</Box>

                <Box sx={styles.statRow}>
                  <IconWrapper
                    name="questions"
                    size="md"
                    parentFolder="icons"
                    type="png"
                    customSx={{
                      width: { xs: pxToRem(20), md: pxTovW(25) },
                      height: { xs: pxToRem(20), md: pxTovW(25) },
                    }}
                  />
                  <Typography variant="h2" sx={styles.statNumber}>
                    {item.attempted}/{item.total}
                  </Typography>
                </Box>

                <Typography variant="cardText" sx={styles.statLabel}>
                  Questions Attempted
                </Typography>
              </Box>

              {/* Render vertical divider between columns, but not after the last one */}
              {/* {index < summaryData.length - 1 && (
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={styles.verticalDivider}
                />
              )} */}
            </React.Fragment>
          ))}
        </Box>
      </Box>
    </Dialog>
  );
};
